﻿// A more performant implementaion of Q promises that is agnostic to async vs. sync
// functions. In Q, a promise is always performed one frame afterwards, whereas here,
// if the wrapped function or value is actually synchronous, it will perform synchronously.
// The side-effect is the exact ordering of operations will be dependent on whether the
// passed value is synchronous or not. With Q, all operations will always be performed
// after the promise is created/returned and other synchronous code is executed. Here,
// synchronous code will be performed BEFORE the sync operations after the promise is
// created, whereas asynchronous code will be performed AFTER.
//
// In other words, any code contained in the promise resolution should NOT assume sync
// code after promise creation has taken effect.
export class KPromise<T> implements Promise<T> {
    
    constructor(value: T) {
        this.then = (onFulfill, onReject) => {
            if (value && !(value as any).$isKPromise && typeof (value as any).then === "function") {
                return (value as any).then(onFulfill, onReject);
            } else if (value && (value as any).$isKPromise) {
                return (value as any).then(onFulfill, onReject);
            } else {
                return new KPromise(onFulfill(value));
            }
        };

        this.catch = (onReject) => {
            const wrapped = value as any;
            if (wrapped && typeof wrapped.then === "function") {
                return typeof wrapped.catch === "function"
                    ? wrapped.catch(onReject)
                    : wrapped.then(undefined, onReject);
            }
            return new KPromise(value) as any;
        };

        this.finally = (onfinally) => {
            const wrapped = value as any;
            if (wrapped && typeof wrapped.then === "function") {
                if (typeof wrapped.finally === "function") return wrapped.finally(onfinally);
                return wrapped.then(
                    result => {
                        if (onfinally) onfinally();
                        return result;
                    },
                    error => {
                        if (onfinally) onfinally();
                        throw error;
                    },
                );
            }
            if (onfinally) onfinally();
            return new KPromise(value) as any;
        };
    }
    [Symbol.toStringTag]: string;
    public $isKPromise = true;
   
    //public then: (onFulfill: (value: T) => any, onReject?: (reason: any) => any) => Q.IPromise<T>;
    public then: <TResult1 = T, TResult2 = never>(
            onfulfilled?: ((value: T) => TResult1 | PromiseLike<TResult1>) | undefined | null, 
            onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | undefined | null
            ) => Promise<TResult1 | TResult2>;

    public catch: <TResult = never>(
        onrejected?: ((reason: any) => TResult | PromiseLike<TResult>) | undefined | null) => Promise<T | TResult>;

    public finally: (onfinally?: (() => void) | undefined | null) => Promise<T>;
}

export class FluentIf {
    constructor(private promise: Q.IPromise<any>) {
    }

    public elseIf(condition: boolean, _cb: () => any): FluentIf {
        // If there is an existing promise, then it means a prior
        // condition was true, so we ignore this one unconditionally.
        if (!this.promise && condition) {
            this.promise = new KPromise(_cb());
        }
        return this;
    }

    public else(_cb: () => any): FluentIf {
        if (!this.promise) {
            this.promise = new KPromise(_cb());
        }
        return this;
    }
        
    // Callback is called after promise is resolved from
    // whatever condition passed.
    public finally(_cb: () => any): Q.IPromise<any> {
        if (!this.promise) {// if no condition was met
            // need to new up a KbPromise so that the consumer can treat the if block like a promise
            if (_cb) return new KPromise(_cb());
        } else { // a condition was met
            return this.promise.then(res => {
                // we assume that if the result of the condition is something other than undefined,
                // then the return block was hit.  So we just return that value.
                // we can assume this because our snap return block is set to generate
                // something like 'return val || null'
                if (res !== undefined) {
                    return res;
                } else {
                    if (_cb) {
                        return _cb();
                    }
                }
            });
        }
    }
}

// Special if implementation which is promise-friendly. Especially useful in
// snap code where we are sort of simulating "await" functionality. Used in
// conjunction with KPromise
export function _if(condition: boolean, _cb: () => any) {
    let promise: Q.IPromise<any>;
    if (condition) promise = new KPromise(_cb());
    return new FluentIf(promise);
}

// Special while implementation which is promise-friendly. Note that this function
// uses recursion, so the while loop is effectively limited to the stack size limit
// for the browser. Tests have shown that the latest browsers have a limit exceeding
// 20,000, so these loops are limited to 20,000 iterations or so (not accounting for
// the base stack overhead, which I imagine should be less than 20 or so).
//
// http://stackoverflow.com/questions/17217736/while-loop-with-promises
//
// http://stackoverflow.com/questions/7826992/browser-javascript-stack-size-limit

export function _while(condition: () => boolean, _cb: () => any) {
    let promise: Q.IPromise<any>;
    if (condition()) promise = new KPromise(_cb());
    else return new KPromise(null);
    return promise.then(_break => {
        if (_break) return;
        return _while(condition, _cb);
    });
}

// Promise-friendly do-while loop. Uses same recusive plan as _while() above.

export function _dowhile(condition: () => boolean, _cb: () => any) {
    let promise: Q.IPromise<any> = new KPromise(_cb());
    if (!condition()) {
        return promise;
    }
    else {
        return promise.then(_break => {
            if (_break) return;
            return _dowhile(condition, _cb);
        });
    }
}

// Promise-friendly for loop. Uses same recusive method as _while() above.

export function _for(initial: () => void, condition: () => boolean, each: () => void, _cb: () => any) {
    initial();
    let promise: Q.IPromise<any> = new KPromise(null);
    if (condition()) promise = new KPromise(_cb());
    else return promise;
    return promise.then(_break => {
        if (_break) {
            return;
        }
        each();
        return _for(() => { }, condition, each, _cb);
    });
}

/**
 * used by while and for loops in snap to make sure we don't end up with an infinite loop
 */
export class Loop {
    constructor(private isAsync = false) {
        this.max = isAsync ? 10000 : 1000000; // we allow far less iterations for async loops
    }
    private max: number;
    private i: number = 0;

    public check() {
        this.i++;
        if (this.i > this.max) {
            // tslint:disable-next-line:max-line-length
            throw new Error(`Infinite loop detected.  A maximum of ${this.max} iterations is allowed for ${this.isAsync ? "an async" : "a"} loop`);
        }
    }
}
